import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META, STATUS_META, imageSrc } from "../lib/api";
import { CheckCircle2, XCircle, Send, MessageCircle, Phone, Loader2, Inbox, CalendarX, Zap, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { ApprovalConsole } from "./ApprovalConsole";

const Chip = ({ status }) => (
  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_META[status]?.cls}`} data-testid="approval-status-badge">
    {STATUS_META[status]?.label || status}
  </span>
);

export const ApprovalFeed = ({ onChange }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState("");
  const [tab, setTab] = useState("waiting");
  const [timing, setTiming] = useState(null);
  const [loadError, setLoadError] = useState("");
  const toggle = (id) => setSelected((cur) => {
    const next = new Set(cur);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Decide many at once. Each is handled on its own server-side, so one that
  // fails to publish is reported without stopping the rest.
  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(action);
    try {
      const { data } = await api.post("/posts/bulk", { ids, action, reason: action === "reject" ? "Rejected in bulk" : "" });
      const n = data.done.length;
      if (n) toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${n} post${n === 1 ? "" : "s"}`);
      if (data.errors.length) toast.error(`${data.errors.length} couldn't be ${action}d: ${data.errors[0].error}`);
      setSelected(new Set());
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed");
    } finally { setBulkBusy(""); }
  };

  // Drafts live on their own page, so they are not fetched here. A failed
  // request shows its reason and a retry instead of spinning forever.
  const load = async () => {
    setLoading(true);
    setLoadError("");
    const [postsRes, settingsRes] = await Promise.allSettled([
      api.get("/posts?exclude=draft,generating&limit=500", { timeout: 60000 }),
      api.get("/settings", { timeout: 30000 }),
    ]);
    if (postsRes.status === "fulfilled") setPosts(postsRes.value.data);
    else setLoadError(postsRes.reason?.response?.data?.detail || postsRes.reason?.message || "Could not load posts");
    if (settingsRes.status === "fulfilled") setTiming(settingsRes.value.data.publish_timing || "now");
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveTiming = async (value) => {
    setTiming(value);
    try {
      const { data: s } = await api.get("/settings");
      await api.put("/settings", { ...s, publish_timing: value });
      toast.success(value === "now" ? "Approved posts now publish straight away" : "Approved posts now wait for their best time");
    } catch { toast.error("Could not save"); }
  };

  const approve = async (p) => {
    setActing(p.id);
    try {
      const { data } = await api.post(`/posts/${p.id}/approve`);
      if (data.status === "scheduled") toast.success("Approved — it will publish at its scheduled time");
      else if (data.status === "failed") toast.error("Approved, but publishing failed - see the reason on the post");
      else toast.success("Approved & published 🎉");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); } finally { setActing(null); }
  };

  const publishNow = async (p) => {
    setActing(p.id);
    try {
      const { data } = await api.post(`/posts/${p.id}/publish-now`);
      data.status === "failed" ? toast.error("Publishing failed - see the reason on the post") : toast.success("Published 🎉");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not publish"); } finally { setActing(null); }
  };

  // Back to waiting for approval, with the time cleared.
  const unschedule = async (ids) => {
    const all = !ids;
    setBulkBusy("cancel");
    try {
      const { data } = await api.post("/schedule/cancel", { ids: ids || [] });
      toast.success(`${data.cancelled} post${data.cancelled === 1 ? "" : "s"} taken off the schedule - back in Waiting`);
      await load();
      if (all) setTab("waiting");
      onChange && onChange();
    } catch { toast.error("Could not cancel"); } finally { setBulkBusy(""); }
  };

  const doReject = async () => {
    setActing(rejectFor.id);
    try {
      await api.post(`/posts/${rejectFor.id}/reject`, { reason: reason || "Not approved" });
      toast("Post rejected");
      setRejectFor(null); setReason("");
      await load();
      onChange && onChange();
    } catch { toast.error("Action failed"); } finally { setActing(null); }
  };

  const submit = async (p) => {
    setActing(p.id);
    try {
      const { data } = await api.post(`/posts/${p.id}/submit`);
      const sent = Object.entries(data.delivery || {}).filter(([, v]) => v.sent).map(([k]) => k);
      toast.success(sent.length ? `Sent via ${sent.join(", ")}` : "Sent to approval queue");
      await load();
      onChange && onChange();
    } catch { toast.error("Submit failed"); } finally { setActing(null); }
  };

  // Drafts live on the Drafts page; this page is decisions and outcomes.
  const pending = posts.filter((p) => p.status === "pending_approval");
  const groups = {
    waiting: pending,
    scheduled: posts.filter((p) => p.status === "scheduled")
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    published: posts.filter((p) => p.status === "posted"),
    failed: posts.filter((p) => p.status === "failed"),
    rejected: posts.filter((p) => p.status === "rejected"),
  };
  const TABS = [["waiting", "Waiting"], ["scheduled", "Scheduled"], ["published", "Published"],
    ["failed", "Failed"], ["rejected", "Rejected"]];

  /* Every channel's real outcome. The old banner counted entries in
   platform_results and called them all published, so a post where every real
   publish had failed still read "Published to 1 channel(s)". */
const PublishResult = ({ results }) => {
  const rows = Object.values(results);
  const failed = rows.filter((r) => r.status === "failed");
  const skipped = rows.filter((r) => r.status === "skipped");
  const real = rows.filter((r) => r.status === "posted" && r.mode === "real");
  const simulated = rows.filter((r) => r.status === "posted" && r.mode === "simulated");

  return (
    <div className="w-full space-y-1.5" data-testid="publish-result">
      {real.length > 0 && (
        <p className="text-xs text-sky-400 font-medium">
          Published to {real.length} account{real.length > 1 ? "s" : ""}
        </p>
      )}
      {simulated.length > 0 && (
        <p className="text-xs text-slate-400 font-medium">
          Simulated for {simulated.length} channel{simulated.length > 1 ? "s" : ""} — not actually posted
        </p>
      )}
      {skipped.map((r, i) => (
        <p key={`s${i}`} className="text-[11px] text-amber-300/90">
          <strong>{r.platform} — {r.account}:</strong> skipped. {r.error}
        </p>
      ))}
      {failed.map((r, i) => (
        <div key={i} className="rounded-lg border border-rose-900/40 bg-rose-950/30 px-2.5 py-2">
          <p className="text-xs text-rose-300 font-semibold">
            {r.platform} — {r.account}: not published
          </p>
          {r.error && <p className="text-[11px] text-rose-400/80 mt-0.5 break-words">{r.error}</p>}
        </div>
      ))}
    </div>
  );
};

const Card = ({ p }) => (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`glass rounded-2xl overflow-hidden relative ${selected.has(p.id) ? "ring-2 ring-indigo-400/70" : ""}`}
      data-testid="approval-card">
      {p.status === "pending_approval" && (
        <label className="absolute top-2 left-2 z-10 w-7 h-7 rounded-lg bg-slate-950/80 border border-slate-600 flex items-center justify-center cursor-pointer"
          title="Select">
          <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
            className="accent-indigo-500 w-4 h-4" data-testid="approval-select" />
        </label>
      )}
      <div className="flex">
        {imageSrc(p) ? (
          <img src={imageSrc(p)} alt="" className="w-28 h-full min-h-[112px] object-cover" />
        ) : (
          <div className="w-28 min-h-[112px] bg-slate-800 flex items-center justify-center text-slate-600 text-2xl">✦</div>
        )}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1.5">
            <p className="text-sm font-semibold text-slate-100 truncate">{p.topic}</p>
            <Chip status={p.status} />
          </div>
          <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">{p.caption}</p>
          <div className="flex items-center gap-2 mt-2.5">
            {p.platforms?.map((pl) => (
              <span key={pl} className="text-[10px] font-semibold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${PLATFORM_META[pl]?.color}22`, color: PLATFORM_META[pl]?.color }}>
                {PLATFORM_META[pl]?.label}
              </span>
            ))}
            {p.scheduled_at && <span className="text-[10px] text-slate-500 ml-auto">{new Date(p.scheduled_at).toLocaleDateString()}</span>}
          </div>
          {p.reject_reason && <p className="text-[11px] text-rose-400 mt-2">Reason: {p.reject_reason}</p>}
          {p.hold_reasons?.length > 0 && p.status !== "posted" && (
            <p className="text-[11px] text-[#F9A8D4] mt-2">Held: {p.hold_reasons[0]}</p>
          )}
          {p.retrying && <p className="text-[11px] text-[#A5B4FC] mt-2">Publishing failed - retrying automatically ({p.publish_attempts} of 3)</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-800 bg-slate-950/40">
        {p.status === "pending_approval" && (
          <>
            <button data-testid="telegram-approve-btn" onClick={() => approve(p)} disabled={acting === p.id}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-xs font-semibold transition-colors disabled:opacity-60">
              {acting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve
            </button>
            <button data-testid="telegram-reject-btn" onClick={() => setRejectFor(p)} disabled={acting === p.id}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 text-xs font-semibold transition-colors">
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
        {p.status === "draft" && (
          <button data-testid="submit-btn" onClick={() => submit(p)} disabled={acting === p.id}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 text-xs font-semibold transition-colors disabled:opacity-60">
            {acting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit for approval
          </button>
        )}
        {(p.status === "posted" || p.status === "failed") && p.platform_results && (
          <PublishResult results={p.platform_results} />
        )}
        {p.status === "scheduled" && (
          <>
            <p className="text-xs text-purple-300 font-medium flex items-center gap-1.5 flex-1 min-w-0">
              <Clock className="w-3.5 h-3.5 shrink-0" /> {new Date(p.scheduled_at).toLocaleString()}
            </p>
            <button onClick={() => publishNow(p)} disabled={acting === p.id}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-[11px] font-semibold">
              {acting === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Publish now
            </button>
            <button onClick={() => unschedule([p.id])} disabled={!!bulkBusy}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:border-slate-600 text-[11px] font-semibold">
              <CalendarX className="w-3 h-3" /> Cancel
            </button>
          </>
        )}
        {p.status === "rejected" && <p className="text-xs text-rose-400 font-medium">Rejected</p>}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-8 min-w-0" data-testid="approvals-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono-label text-ac-link mb-2">Queue</p>
          <h1 className="font-display text-3xl font-semibold text-ac-text">Approvals</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {timing && (
            <label className="flex items-center gap-2 glass rounded-full pl-4 pr-2 py-1.5 text-xs text-slate-400" data-testid="publish-timing">
              When approved
              <select value={timing} onChange={(e) => saveTiming(e.target.value)}
                className="bg-slate-950/60 border border-slate-800 rounded-full px-3 py-1 text-xs text-slate-100 focus:outline-none">
                <option value="now">Publish right away</option>
                <option value="best_time">Wait for the best time</option>
              </select>
            </label>
          )}
          <div className="hidden sm:flex items-center gap-3 glass rounded-full px-4 py-2">
            <MessageCircle className="w-4 h-4" style={{ color: "#0088CC" }} />
            <Phone className="w-4 h-4" style={{ color: "#25D366" }} />
            <span className="text-xs text-slate-400">Telegram · WhatsApp routing</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-ac-border" data-testid="approval-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`relative px-3 py-1.5 text-[13px] font-medium ${tab === k
              ? "text-ac-text" : "text-ac-muted hover:text-ac-text"}`}>
            {l} <span className="font-mono text-[12px] opacity-70">{groups[k].length}</span>
            {/* The active-tab underline is one of the cruise line's allowed places. */}
            {tab === k && <span className="cruise-line absolute left-3 right-3 -bottom-px" />}
          </button>
        ))}
      </div>

      {loading && !posts.length ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : loadError ? (
        <div className="glass rounded-2xl p-10 flex flex-col items-center gap-3 text-center">
          <XCircle className="w-8 h-8 text-rose-400" />
          <p className="text-sm text-slate-300">The approvals list could not be loaded.</p>
          <p className="text-xs text-slate-500 max-w-md">{loadError}</p>
          <button onClick={load} className="mt-1 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold">Try again</button>
        </div>
      ) : (
        <>
          {tab === "scheduled" && groups.scheduled.length > 0 && (
            <div className="glass rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3">
              <p className="text-xs text-slate-400 flex-1 min-w-[220px]">
                These publish on their own at the time shown. Cancelling sends them back to Waiting - nothing is deleted.
              </p>
              <button onClick={() => unschedule(null)} disabled={!!bulkBusy} data-testid="cancel-all-schedules"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 text-xs font-semibold disabled:opacity-40">
                {bulkBusy === "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarX className="w-3.5 h-3.5" />}
                Cancel all {groups.scheduled.length} scheduled
              </button>
            </div>
          )}
          {tab === "waiting" && pending.length > 0 && (
            <div className="sticky top-14 md:top-2 z-20 glass rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" className="accent-indigo-500 w-4 h-4"
                  checked={selected.size > 0 && pending.every((p) => selected.has(p.id))}
                  onChange={(e) => setSelected(e.target.checked ? new Set(pending.map((p) => p.id)) : new Set())} />
                Select all {pending.length}
              </label>
              <span className="text-xs text-slate-500">{selected.size} selected</span>
              <div className="ml-auto flex gap-2">
                <button onClick={() => bulk("approve")} disabled={!selected.size || !!bulkBusy} data-testid="bulk-approve"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 text-xs font-semibold disabled:opacity-40">
                  {bulkBusy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve selected
                </button>
                <button onClick={() => bulk("reject")} disabled={!selected.size || !!bulkBusy}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 text-xs font-semibold disabled:opacity-40">
                  {bulkBusy === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Reject selected
                </button>
              </div>
            </div>
          )}
          {tab === "waiting" && pending.length > 0 ? (
            <ApprovalConsole posts={pending} onDone={() => { load(); onChange && onChange(); }} />
          ) : <Section items={groups[tab]} Card={Card} empty={{
            waiting: "Nothing waiting — you're all caught up.",
            scheduled: "Nothing is scheduled. Approved posts publish straight away unless you choose \"Wait for the best time\".",
            published: "Nothing published yet.", failed: "No failed posts.", rejected: "Nothing rejected.",
          }[tab]} />}
        </>
      )}

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading">Reject post</DialogTitle></DialogHeader>
          <textarea data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Reason (optional)" className="w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-rose-500/60" />
          <DialogFooter>
            <button onClick={() => setRejectFor(null)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button data-testid="confirm-reject-btn" onClick={doReject} className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold">Reject post</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ items, Card, empty }) => (
  <div>
    {items.length ? (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{items.map((p) => <Card key={p.id} p={p} />)}</div>
    ) : (
      <div className="glass rounded-2xl p-10 flex flex-col items-center gap-2 text-slate-500">
        <Inbox className="w-8 h-8" /><span className="text-sm">{empty}</span>
      </div>
    )}
  </div>
);
